'use client';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { DEFAULT_CHATGPT_MESSAGE } from '@components/ChatGPTLoader/utils';
import { callService } from '@api/remote-call';
import { ChatbotStatus } from '@api/v3';

import { createGenericContext } from '../generateContext';

type ContextInterface = {
  chatGPTMessage?: string;
  setChatGPTMessage: Dispatch<SetStateAction<string>>;
  visibleChatGPT?: boolean;
  setVisibleChatGPT: Dispatch<SetStateAction<boolean>>;
  visibleChatGPTTooltip?: boolean;
  setVisibleChatGPTTooltip: Dispatch<SetStateAction<boolean>>;
  changeChatGPTVisibility: (condition: boolean) => void;
  chatbotStatus: boolean;
  setChatBotStatus: Dispatch<SetStateAction<boolean>>;
  setItemId: Dispatch<SetStateAction<number | null>>;
  itemId: number | null;
  itemType: 'Hotel' | 'Tour' | '';
  setItemType: Dispatch<SetStateAction<'Hotel' | 'Tour' | ''>>;
};

const [useChatGPTContext, ChatGPTContextProvider] =
  createGenericContext<ContextInterface>();

const ChatGPTProvider = (
  props: React.PropsWithChildren<unknown>
): React.JSX.Element => {
  const [chatGPTMessage, setChatGPTMessage] = useState(DEFAULT_CHATGPT_MESSAGE);
  const [chatbotStatus, setChatBotStatus] = useState(false);
  const [visibleChatGPT, setVisibleChatGPT] = useState(true);
  const [visibleChatGPTTooltip, setVisibleChatGPTTooltip] = useState(false);
  const [itemId, setItemId] = useState<number | null>(null);
  const [itemType, setItemType] = useState<'Hotel' | 'Tour' | ''>('');

  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData(['status']);

  const { data } = useQuery({
    queryKey: ['status'],
    queryFn: () =>
      callService({
        service: 'ContentsService',
        version: 'v3',
        method: 'getChatbotStatus',
      }),
    enabled: !cachedData,
    staleTime: 1000 * 60 * 30,
    cacheTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (data) {
      if (data.web === ChatbotStatus.HIDE) {
        setChatBotStatus(false);
      }
      if (data.web === ChatbotStatus.SHOW) {
        setChatBotStatus(true);
      }
    }
  }, [data]);

  const changeChatGPTVisibility = (condition: boolean) => {
    setVisibleChatGPT(condition);
  };

  return (
    <ChatGPTContextProvider
      value={{
        chatGPTMessage,
        setChatGPTMessage,
        visibleChatGPT,
        setVisibleChatGPT,
        visibleChatGPTTooltip,
        setVisibleChatGPTTooltip,
        changeChatGPTVisibility,
        chatbotStatus,
        setChatBotStatus,
        setItemId,
        itemId,
        itemType,
        setItemType,
      }}
    >
      {props.children}
    </ChatGPTContextProvider>
  );
};
export { useChatGPTContext, ChatGPTProvider };
